import { type Institution } from '../../services/institutions';
import { type ProviderUser, type Purchase } from '../../services/users';
import { type NodeRoomV2 } from '../../services/room';
import SegmentTracker from './trackers/segmentTracker';
import UserpilotTracker from './trackers/userpilotTracker';
import { UserPlanType } from '../../hooks/useUserSubscription/useUserSubscription.types';

export type Properties = Record<string, unknown>;

export type SignUpPlanType = 'free' | 'pro' | 'premium';

export type Tracker = {
  id: string;
  identify: (
    user?: ProviderUser,
    institution?: Institution,
    latestPurchase?: Purchase,
    rooms?: NodeRoomV2[],
    currentPlan?: UserPlanType,
    userRole?: string,
    authId?: string,
    tenantId?: string,
  ) => void;
  track: (event: TrackedEvents, properties?: Properties) => Promise<void>;
  reset?: () => void;
};

export const TRACKERS: () => Tracker[] = () => [
  SegmentTracker,
  UserpilotTracker,
];

// Event values should be agreed with #intelligence-team as they will be tracked in Segment
export enum TrackedEvents {
  /* Sessions Events */
  BackgroundEffectApplied = 'Background Effect Applied',
  CallHoldLaunched = 'Call Hold Launched',
  CallHoldResumed = 'Call Hold Resumed',
  CallRated = 'Call Rated',
  FullscreenToggled = 'Fullscreen Toggled',
  LiveChatMessageSent = 'Live Chat Message Sent',
  ParticipantsAddedToCall = 'Participants Added To Call',
  PictureInPictureToggled = 'Picture In Picture Toggled',
  ScreenshareEnded = 'Screenshare Ended',
  ScreenshareInitiated = 'Screenshare Initiated',
  ScreenshareStarted = 'Screenshare Started',
  ScreenshareZoomChanged = 'Screenshare Zoom Changed',
  ScreenshareRequestCancelled = 'Screenshare Request Cancelled',
  ScreenshareRequestRejected = 'Screenshare Request Rejected',
  ScreenshareRequested = 'Screenshare Requested',
  StartCall = 'Session Started',
  EndCall = 'Session Closed',
  TestPatientAdded = 'Test Patient Added',
  StartTestCall = 'Test Session Started',
  EndTestCall = 'Test Session Closed',
  TroubleshootingInitiated = 'Troubleshooting Initiated',

  /* Sessions Events */

  /* Check-in Events */
  /* Classic Check-in */
  CheckInCompleted = 'Checkin Completed',
  CheckInStarted = 'Checkin Started',
  RoomEntered = 'Room Entered',
  /* New Check-in Experience */
  CheckInModalLoaded = 'Checkin Modal Loaded',
  CameraTested = 'Camera Tested',
  CheckInDeviceAccessTroubleshootingInitiated = 'Checkin Device Access Troubleshooting Initiated',
  MediaAccessRequested = 'Media Access Requested',
  MicrophoneTested = 'Microphone Tested',
  NameEntered = 'Name Entered',
  SpeakerTested = 'Speaker Tested',
  /* Check-in Events */

  /* Extension Events */
  AppLaunched = 'App Launched',
  AppsPageVisited = 'Apps Page Visited',
  AppCardOpened = 'App Card Opened',
  AppInstallStarted = 'App Install Started',
  AppInstalled = 'App Installed',
  AppRemovalStarted = 'App Removal Started',
  AppRemoved = 'App Removed',
  AppInformationOpened = 'App Information Opened',
  /* Prototypes */
  PrototypesPageVisited = 'Prototypes Page Visited',
  AppLibrarySortChanged = 'App Library Sort Changed',
  AccessPrototypesModalOpened = 'Access Prototypes Modal Opened',
  AccessPrototypesModalClosed = 'Access Prototypes Modal Closed',
  AccessPrototypesOptInCompleted = 'Access Prototypes Opt-In Completed',
  /* Photo Capture */
  PhotoCaptureStarted = 'Photo Capture Started',
  PhotoCapturePhotoTaken = 'Photo Capture Photo Taken',
  PhotoCaptureRetakeClicked = 'Photo Capture Retake Clicked',
  PhotoCaptureDownloadClicked = 'Photo Capture Download Clicked',
  /* File Transfer */
  FileTransferStarted = 'File Transfer Started', // When the file(s) upload begins.
  FileTransferFinished = 'File Transfer Finished', // When the file(s) upload is completed (sender side).
  FileTransferAccepted = 'File Transfer Accepted', // When all of the files have been downloaded by the receiver.
  /* Extension Events */

  /* Dashboard Events */
  PrecallTestStarted = 'Precall Test Started',
  PrecallTestButtonClicked = 'Precall test button clicked',
  PatientInvited = 'Patient Invited',
  /* Dashboard patient queue */
  QueueFiltered = 'Queue Filtered',
  QueueSorted = 'Queue Sorted',
  /* Dashboard Events */

  /* Custom Waiting Room */
  WaitingRoomEditorOpened = 'Waiting Room Editor Opened',
  WaitingRoomUpdated = 'Waiting Room Updated',
  WaitingRoomPreviewed = 'Waiting Room Previewed',
  /* Role Selection */
  ProviderOptionClicked = 'Provider Option Clicked',
  PatientOptionClicked = 'Patient Option Clicked',
  ProviderSelectionContinueClicked = 'Provider Selection Continue Clicked',
  PatientSelectionContinueClicked = 'Patient Selection Continue Clicked',

  UpgradeButtonClicked = 'Upgrade Button Clicked',
  UpgradeAbandonedDuringPlanOverviewPage = 'Upgrade Abandoned During Plan Overview Page',
  UpgradeAbandonedDuringPaymentSummaryPage = 'Upgrade Abandoned During Payment Summary Page',
  SubscriptionCycleChangedDuringPlanOverviewPage = 'Subscription Cycle Changed During Plan Overview Page',
  SubscriptionCycleChangedDuringPaymentSummaryPage = 'Subscription Cycle Changed During Payment Summary Page',
  DayPassTooltipViewedOnUpgradePlanOverviewPage = 'Day Pass Tooltip Viewed On Upgrade Plan Overview Page',
  DayPassPlanSelected = 'Day Pass Plan Selected',
  UpgradePlanSelectedToMoveToPaymentSummaryPage = 'Upgrade Plan Selected To Move To Payment Summary Page',
  BackButtonClickedOnPaymentSummaryPage = 'Back Button Clicked on Payment Summary Page',
  UpgradePromoCodeAddedSuccessfully = 'Upgrade Promo Code Added Successfully',
  UpgradePromoCodeSelectedInvalid = 'Invalid Upgrade Promo Code Selected',
  UpgradePromoCodeRemoved = 'Upgrade Promo Code Removed',
  UpgradeRecaptchaCompletedSuccessfully = 'Upgrade Recaptcha Completed Successfully',
  UpgradeRecaptchaFailed = 'Upgrade Recaptcha Failed',
  SubscribeButtonClickedToMoveToConfirmationPage = 'Subscribe Button Clicked To Move To Confirmation Page',
  StripeSourceAddedSuccessfullyDuringUpgrade = 'Stripe Source Added Successfully During Upgrade',
  StripeSourceFailedToAddDuringUpgrade = 'Stripe Source Failed to Add During Upgrade',
  UpgradePaymentInitiatedSuccessfully = 'Upgrade Payment Initiated Successfully',
  UpgradePaymentFailedToInitiate = 'Upgrade Payment Failed To Initiate',
  UpgradePaymentSuccessfullyConfirmed = 'Upgrade Payment Successfully Confirmed',
  UpgradePaymentFailedToConfirm = 'Upgrade Payment Failed to Confirm',
  UpgradePaymentCompletedSuccessfully = 'Upgrade Payment Completed Successfully',
  UpgradePaymentFailedToComplete = 'Upgrade Payment Failed to Complete',
  UpgradeFlowCompleted = 'Upgrade Flow Completed',
  ClinicSetupCompleted = 'Clinic Setup Completed',
  ClinicSetupAbandoned = 'Clinic Setup Abandoned',
  SeatsTooltipOnUpgradePaymentSummaryPageViewed = 'Seats Tooltip On Upgrade Payment Summary Page Viewed',
  SetupFeeTooltipOnUpgradePaymentSummaryPageViewed = 'Setup Fee Tooltip On Upgrade Payment Summary Page Viewed',
  SalesTaxTooltipOnUpgradePaymentSummaryPageViewed = 'Sales Tax Tooltip On Upgrade Payment Summary Page Viewed',

  /* Sign Up Events */
  EmailEnteredAtSignUp = 'Email Entered At Sign Up',
  /** plan_type: free | pro | premium | clinic (see validateAccountType) */
  SignUpOnboardingComplete = 'Sign Up Onboarding Complete',
  /** plan_type: pro | premium | clinic (paid signup checkout; see validateAccountType) */
  SignUpPaymentComplete = 'Sign Up Payment Complete',

  /* Sign Up Events */
  WhatsNewClicked = "What's New Clicked",

  // Performance
  HighCpuDetected = 'High CPU Detected',

  ButtonClicked = 'Button Clicked',

  AvatarUploaded = 'Avatar Uploaded',

  /** Patient Transfer */
  PatientTransferInitiated = 'Patient Transfer Initiated',
  PatientTransferDismissed = 'Patient Transfer Dismissed',
  PatientTransferStarted = 'Patient Transfer Started',
  PatientTransferCompleted = 'Patient Transfer Completed',
  PatientTransferFailed = 'Patient Transfer Failed',
  PatientTransferCanceled = 'Patient Transfer Canceled',
}

export const identify = (
  user?: ProviderUser,
  institution?: Institution,
  latestPurchase?: Purchase,
  rooms?: NodeRoomV2[],
  currentPlan?: UserPlanType,
  userRole?: string,
  authId?: string,
  tenantId?: string,
): void => {
  void Promise.all(
    TRACKERS().map(async (tracker) => {
      tracker.identify(
        user,
        institution,
        latestPurchase,
        rooms,
        currentPlan,
        userRole,
        authId,
        tenantId,
      );
    }),
  );
};

export const track = async (
  event: TrackedEvents,
  properties?: Properties,
): Promise<void> => {
  await Promise.all(
    TRACKERS().map(async (tracker) => tracker.track(event, properties)),
  );
};

export const getAnonymousId = (): string | undefined => {
  const { analytics } = window;
  return analytics?.user?.() ? analytics.user().anonymousId() : undefined;
};

export const reset = (): void => {
  TRACKERS().forEach((tracker) => {
    if (tracker.reset) {
      tracker.reset();
    }
  });
  void import('../../hooks/usePostHog').then((m) => m.resetPostHog());
};
